import { useAppSelector } from 'store/hooks';
import * as React from 'react';
import * as Yup from 'yup';
import { Form } from 'formik';
import { Box, Button, Grid, Typography } from '@mui/material';

import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { MuiForm } from 'lib/Form';
import { CheckboxFormField } from 'lib/form/fields/CheckboxFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { Option } from 'lib/types';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { useDiscountIds } from '../hooks/useDiscountIds';
import { AdjustmentSchemeRedemptionFormStep } from './AdjustmentSchemeEditStepper';
import { AdjustmentScheme } from '../adjustmentSchemeModel';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';

export interface ConfigureSchemeGeneralFormData {
    title: string;
    internalTitle?: string;
    schemeId: string;
    externalPosId?: string;
    locationIds: string[];
    enabled: boolean;
    excludeDiscountIds?: string[];
}

interface AdjustmentSchemeGeneralFormProps {
    open: boolean;
    existingSchemes: AdjustmentScheme[];
    initialValues?: ConfigureSchemeGeneralFormData;
    isLoading?: boolean;
    isFormValid?: boolean;
    isEdit?: boolean;
    onUpdate?: (values: {
        type: AdjustmentSchemeRedemptionFormStep.GENERAL;
        values: ConfigureSchemeGeneralFormData;
    }) => void;
    onSubmit: (data: ConfigureSchemeGeneralFormData) => void;
}

export const configureSchemeGeneralFormDefaultValues: ConfigureSchemeGeneralFormData = {
    title: '',
    internalTitle: '',
    schemeId: '',
    externalPosId: '',
    locationIds: [],
    enabled: true,
    excludeDiscountIds: []
};

const popperProps = {
    sx: {
        '& .MuiAutocomplete-listbox': {
            maxHeight: '200px'
        }
    }
};

const validationSchema = (existingCodes: string[]) =>
    Yup.object().shape({
        title: Yup.string().required('Title is required.'),
        internalTitle: Yup.string(),
        schemeId: Yup.string()
            .required('Scheme ID is required.')
            .notOneOf(existingCodes, 'Scheme ID already exists.'),
        locationIds: Yup.array().of(Yup.string()).required('Location is required.'),
        enabled: Yup.boolean().required('Enabled is required.')
    });

export const AdjustmentSchemeGeneralForm = ({
    open,
    isEdit,
    initialValues,
    existingSchemes,
    isFormValid,
    isLoading,
    onSubmit,
    onUpdate
}: AdjustmentSchemeGeneralFormProps) => {
    const { allLocations } = useLocationHelpers();

    const { perks: existingPerks } = useAppSelector(state => state.perks);
    const { discountIds, loading: discountIdsLoading } = useDiscountIds({
        adjustmentSchemes: existingSchemes,
        perks: existingPerks.length > 0 ? existingPerks : undefined
    });

    const locationOptions: Option[] = React.useMemo(() => {
        if (allLocations) {
            return allLocations
                .filter(location => location.state === 'ACTIVE')
                .map(location => ({ value: location._id, label: location.title }));
        }

        return [];
    }, [allLocations]);

    const existingCodes = React.useMemo(() => existingSchemes.map(s => s.schemeId), [existingSchemes]);

    if (!open) {
        return null;
    }

    return (
        <MuiForm
            initialValues={initialValues || configureSchemeGeneralFormDefaultValues}
            validationSchema={validationSchema(existingCodes)}
            onSubmit={onSubmit}
        >
            {({ submitForm, isValid, values }) => {
                const handleUpdate = () => {
                    onUpdate({ values, type: AdjustmentSchemeRedemptionFormStep.GENERAL });
                };
                return (
                    <Form>
                        <Box maxWidth={600}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Box padding={2}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <Typography variant="h4">1. Define</Typography>
                                            </Grid>
                                            <Grid size={12}>
                                                <TextFormField
                                                    name="title"
                                                    label={<MuiFormLabel required>Title</MuiFormLabel>}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <TextFormField
                                                    name="internalTitle"
                                                    placeholder="Optional"
                                                    label="Internal Title"
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <TextFormField
                                                    name="schemeId"
                                                    label={<MuiFormLabel required>Scheme ID</MuiFormLabel>}
                                                    disabled={isEdit}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <TextFormField
                                                    name="externalPosId"
                                                    placeholder="Optional"
                                                    label={<MuiFormLabel>POS Adjustment Code</MuiFormLabel>}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <AutocompleteFormField
                                                    name="locationIds"
                                                    placeholder="123abc"
                                                    multiple
                                                    hasAllOption
                                                    selectAllOptionLabel="Select All Locations"
                                                    label="Locations"
                                                    options={locationOptions}
                                                    popperProps={popperProps}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <AutocompleteFormField
                                                    name="excludeDiscountIds"
                                                    multiple
                                                    hasAllOption
                                                    selectAllOptionLabel="Select All Perks and Adjustment Schemes"
                                                    options={discountIds}
                                                    disabled={discountIdsLoading}
                                                    placeholder="Search"
                                                    popperProps={popperProps}
                                                    label={
                                                        <MuiFormLabel>
                                                            Excluded Perks or Adjustments
                                                        </MuiFormLabel>
                                                    }
                                                    // eslint-disable-next-line max-len
                                                    description="Select the perks and/or adjustment schemes that cannot be used in conjunction with this adjustment in a single order."
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <CheckboxFormField
                                                    name="enabled"
                                                    label="Enable & Push Live"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Row flex={1} align="flex-end" gutter>
                                        {isEdit && (
                                            <LoadingButton
                                                disabled={isLoading || !isFormValid || !isValid}
                                                loading={isLoading}
                                                // eslint-disable-next-line react/jsx-no-bind
                                                onClick={handleUpdate}
                                                color="primary"
                                                variant="outlined"
                                            >
                                                Update & Close
                                            </LoadingButton>
                                        )}
                                        <Button onClick={submitForm} color="primary" variant="contained">
                                            Next
                                        </Button>
                                    </Row>
                                </Grid>
                            </Grid>
                        </Box>
                    </Form>
                );
            }}
        </MuiForm>
    );
};
